/**
 * Units of measure: purchase unit ↔ stock unit.
 *
 * Every item has one stock unit (`items.base_uom`) that inventory, BOMs and
 * production count in. A vendor may sell it in a different unit: webbing
 * strap is stocked in inches but bought by the roll, and one roll is 150 ft.
 * `unitsPerPurchaseUom` (1,800 here) is the only number conversions use; the
 * "150 ft" is what the buyer reads off the quote and is kept for display.
 */

interface UnitDef {
  dimension: 'length' | 'weight' | 'volume' | 'count';
  /** Size in the dimension's reference unit (in, oz, fl oz, each). */
  factor: number;
  label: string;
}

const UNITS: Record<string, UnitDef> = {
  in: { dimension: 'length', factor: 1, label: 'in' },
  ft: { dimension: 'length', factor: 12, label: 'ft' },
  yd: { dimension: 'length', factor: 36, label: 'yd' },
  mm: { dimension: 'length', factor: 1 / 25.4, label: 'mm' },
  cm: { dimension: 'length', factor: 1 / 2.54, label: 'cm' },
  m: { dimension: 'length', factor: 1 / 0.0254, label: 'm' },
  oz: { dimension: 'weight', factor: 1, label: 'oz' },
  lb: { dimension: 'weight', factor: 16, label: 'lb' },
  g: { dimension: 'weight', factor: 1 / 28.349523125, label: 'g' },
  kg: { dimension: 'weight', factor: 1000 / 28.349523125, label: 'kg' },
  floz: { dimension: 'volume', factor: 1, label: 'fl oz' },
  qt: { dimension: 'volume', factor: 32, label: 'qt' },
  gal: { dimension: 'volume', factor: 128, label: 'gal' },
  ml: { dimension: 'volume', factor: 1 / 29.5735295625, label: 'ml' },
  l: { dimension: 'volume', factor: 1000 / 29.5735295625, label: 'l' },
  each: { dimension: 'count', factor: 1, label: 'each' },
  dozen: { dimension: 'count', factor: 12, label: 'dozen' },
};

const ALIASES: Record<string, string> = {
  '"': 'in',
  inch: 'in',
  inches: 'in',
  "'": 'ft',
  foot: 'ft',
  feet: 'ft',
  yard: 'yd',
  yards: 'yd',
  yds: 'yd',
  millimeter: 'mm',
  millimeters: 'mm',
  centimeter: 'cm',
  centimeters: 'cm',
  meter: 'm',
  meters: 'm',
  metre: 'm',
  metres: 'm',
  ounce: 'oz',
  ounces: 'oz',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  gram: 'g',
  grams: 'g',
  kilogram: 'kg',
  kilograms: 'kg',
  kgs: 'kg',
  'fl oz': 'floz',
  'fl. oz': 'floz',
  quart: 'qt',
  quarts: 'qt',
  gallon: 'gal',
  gallons: 'gal',
  milliliter: 'ml',
  milliliters: 'ml',
  liter: 'l',
  liters: 'l',
  litre: 'l',
  litres: 'l',
  ea: 'each',
  pc: 'each',
  pcs: 'each',
  piece: 'each',
  pieces: 'each',
  unit: 'each',
  units: 'each',
};

/** Units the vendor commonly sells in — suggestions for the purchase unit field. */
export const PURCHASE_UOM_SUGGESTIONS = [
  'roll',
  'spool',
  'box',
  'case',
  'carton',
  'pack',
  'bag',
  'sheet',
  'pallet',
  'each',
] as const;

/** Units a pack size is usually stated in — suggestions for the "contains" field. */
export const SIZE_UOM_SUGGESTIONS = ['in', 'ft', 'yd', 'm', 'cm', 'each', 'lb', 'oz', 'kg', 'g', 'gal', 'l'] as const;

function unitKey(uom: string | null | undefined): string | null {
  if (!uom) return null;
  const raw = uom.trim().toLowerCase().replace(/\.$/, '');
  if (!raw) return null;
  if (UNITS[raw]) return raw;
  return ALIASES[raw] ?? null;
}

export function sameUom(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const ka = unitKey(a);
  const kb = unitKey(b);
  if (ka && kb) return ka === kb;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** How many `to` are in one `from` (1 ft → 12 in), or null when the units don't convert. */
export function unitFactor(from: string | null | undefined, to: string | null | undefined): number | null {
  if (sameUom(from, to)) return 1;
  const a = unitKey(from);
  const b = unitKey(to);
  if (!a || !b) return null;
  const ua = UNITS[a];
  const ub = UNITS[b];
  if (ua.dimension !== ub.dimension) return null;
  return ua.factor / ub.factor;
}

const ABBREVIATIONS = new Set(
  [...Object.values(UNITS).map((u) => u.label), ...Object.keys(ALIASES).filter((a) => a.length <= 3)].map((u) =>
    u.toLowerCase(),
  ),
);

/** "roll" → "rolls", "box" → "boxes"; abbreviations (in, ft, kg, each) stay as they are. */
export function pluralUom(uom: string, qty: number): string {
  const u = uom.trim();
  if (!u || Math.abs(qty) === 1) return u;
  const lower = u.toLowerCase();
  if (ABBREVIATIONS.has(lower) || lower.endsWith('s') || lower === 'each') return u;
  if (/(x|ch|sh|z)$/.test(lower)) return `${u}es`;
  if (/[^aeiou]y$/.test(lower)) return `${u.slice(0, -1)}ies`;
  return `${u}s`;
}

const QTY = new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 });

export function formatQty(qty: number): string {
  return QTY.format(qty);
}

/** "4 rolls", "1,800 in", "1 each". */
export function formatQtyUom(qty: number, uom: string | null | undefined): string {
  const u = (uom ?? '').trim();
  return u ? `${formatQty(qty)} ${pluralUom(u, qty)}` : formatQty(qty);
}

/** Stock units for a quantity in an ordered unit; whole units, like on-hand. */
export function toStockQty(qty: number, unitsPerPurchaseUom: number | null | undefined): number {
  return Math.round(qty * (unitsPerPurchaseUom || 1));
}

/** True when a purchase unit is a real conversion (not just the stock unit again). */
export function isPackUnit(conversion: number | null | undefined): boolean {
  return conversion != null && Number.isFinite(conversion) && Math.abs(conversion - 1) > 1e-9;
}

export interface PurchaseUnit {
  /** roll / box / … ; null = the stock unit. */
  purchaseUom: string | null;
  /** 150 (as the vendor states it). */
  size: number | null;
  /** ft */
  sizeUom: string | null;
  /** Stock units in one purchase unit, e.g. 1800. */
  unitsPerPurchaseUom: number;
}

interface SupplierUnitColumns {
  purchase_uom: string | null;
  purchase_uom_size: number | null;
  purchase_uom_size_uom: string | null;
  units_per_purchase_uom: number | null;
}

export function supplierPurchaseUnit(row: SupplierUnitColumns | null | undefined): PurchaseUnit | null {
  if (!row?.purchase_uom) return null;
  return {
    purchaseUom: row.purchase_uom,
    size: row.purchase_uom_size == null ? null : Number(row.purchase_uom_size),
    sizeUom: row.purchase_uom_size_uom,
    unitsPerPurchaseUom: Number(row.units_per_purchase_uom ?? 1) || 1,
  };
}

/** The purchase unit people think in for an item: the primary supplier's, else any supplier's. */
export function mainPurchaseUnit(
  suppliers: Array<SupplierUnitColumns & { is_primary?: boolean }> | null | undefined,
): PurchaseUnit | null {
  const list = (suppliers ?? []).filter(
    (s) => s.purchase_uom && isPackUnit(Number(s.units_per_purchase_uom ?? 1)),
  );
  return supplierPurchaseUnit(list.find((s) => s.is_primary) ?? list[0]);
}

/** The unit an order is written in: the vendor's purchase unit, else the stock unit. */
export function orderUom(unit: Pick<PurchaseUnit, 'purchaseUom'> | null | undefined, baseUom: string): string {
  return unit?.purchaseUom?.trim() || baseUom;
}

/**
 * "1 roll = 150 ft = 1,800 in". Skips the middle step when the vendor's
 * size is already in the stock unit ("1 box = 100 each").
 */
export function describePurchaseUnit(unit: PurchaseUnit, baseUom: string): string | null {
  const uom = unit.purchaseUom?.trim();
  if (!uom || !isPackUnit(unit.unitsPerPurchaseUom)) return null;
  const stock = formatQtyUom(unit.unitsPerPurchaseUom, baseUom);
  if (unit.size != null && unit.sizeUom && !sameUom(unit.sizeUom, baseUom)) {
    return `1 ${uom} = ${formatQtyUom(unit.size, unit.sizeUom)} = ${stock}`;
  }
  return `1 ${uom} = ${stock}`;
}

/**
 * Stock units read back in purchase units, so a count of inches is easy
 * to picture: 5,400 in → "3 rolls", 4,000 in → "2 rolls + 400 in".
 */
export function breakdownInPurchaseUnits(
  stockQty: number,
  unitsPerPurchaseUom: number,
  purchaseUom: string,
  baseUom: string,
): string | null {
  if (!isPackUnit(unitsPerPurchaseUom) || unitsPerPurchaseUom <= 0 || !Number.isFinite(stockQty)) return null;
  const sign = stockQty < 0 ? '−' : '';
  const abs = Math.abs(stockQty);
  const whole = Math.floor(abs / unitsPerPurchaseUom + 1e-9);
  const rest = Math.round((abs - whole * unitsPerPurchaseUom) * 1000) / 1000;
  if (whole === 0) return `${sign}${formatQty(abs / unitsPerPurchaseUom)} ${pluralUom(purchaseUom, 0.5)}`;
  if (rest === 0) return `${sign}${formatQtyUom(whole, purchaseUom)}`;
  return `${sign}${formatQtyUom(whole, purchaseUom)} + ${formatQtyUom(rest, baseUom)}`;
}

const PRECISE_USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

/** Cost per stock unit, which can be a fraction of a cent: 2.5¢ → "$0.025". */
export function formatUnitCostCents(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return PRECISE_USD.format(cents / 100);
}
